import { Children, cloneElement, ReactElement, ReactNode, useCallback, KeyboardEvent } from 'react';

import { useSnapshot } from '@pukka/valtio';

import { useSelectModalStateContext } from '../SelectModal.state';

interface InjectedProps<Event> {
  readonly onClick: (...event: Event[]) => void;
  readonly selected: boolean;
}

interface SelectModalToggleAllFunctionProps<Event> {
  readonly asChild?: never;
  readonly children: ((props: InjectedProps<Event>) => JSX.Element) | ReactNode;
}

interface SelectModalToggleAllAsChildProps<Event> {
  readonly asChild: true;
  readonly children: ReactElement<Partial<InjectedProps<Event>>>;
}

type SelectItemProps<Event> = SelectModalToggleAllFunctionProps<Event> | SelectModalToggleAllAsChildProps<Event>;

function SelectModalToggleAll<T, E>({ asChild, children }: SelectItemProps<E>) {
  const state = useSelectModalStateContext<T>();
  const snapped = useSnapshot(state);
  const selected = snapped.isAllSelected;

  const onClick = useCallback(() => {
    state.onToggleAll();
  }, [state]);

  const onKeyUpHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        onClick();
      }
    },
    [onClick],
  );

  if (asChild) {
    return (
      <>
        {Children.map(children, (child) => {
          const originalOnClick = child.props.onClick;

          return cloneElement(child, {
            onClick(...args: E[]) {
              originalOnClick?.(...args);
              onClick();
            },
            selected,
          });
        })}
      </>
    );
  }

  if (typeof children === 'function') {
    return children({
      onClick,
      selected,
    });
  }

  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyUp={onKeyUpHandler} data-select-modal-toggle-all-selected={selected}>
      {children}
    </div>
  );
}

export { SelectModalToggleAll };
